import React, { useMemo, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthFetch } from '../hooks/useAuthFetch';

export type TransferKind = 'job' | 'resume';

interface Props {
  open: boolean;
  kind: TransferKind;
  itemId: string;
  itemLabel: string;
  currentOwnerUid: string | null | undefined;
  onClose: () => void;
  onTransferred: (newOwnerUid: string, newOwnerDisplay: string) => void;
}

/**
 * Admin-only modal to reassign ownership of a job or resume to another
 * member of the org. Mirrors the standard ATS recovery path for items
 * whose original owner has left the team.
 */
const TransferOwnershipModal: React.FC<Props> = ({
  open,
  kind,
  itemId,
  itemLabel,
  currentOwnerUid,
  onClose,
  onTransferred,
}) => {
  const { org } = useTenant();
  const authFetch = useAuthFetch();

  const [selectedUid, setSelectedUid] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eligible = useMemo(() => {
    const list = org?.members || [];
    return list
      .filter(m => m.userId && m.userId !== currentOwnerUid)
      .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
  }, [org?.members, currentOwnerUid]);

  if (!open) return null;

  const url =
    kind === 'job'
      ? `/api/job-descriptions/${itemId}/transfer-ownership`
      : `/api/resumes/${itemId}/transfer-ownership`;

  async function submit() {
    if (!selectedUid) {
      setError('Pick a member to transfer to.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerUid: selectedUid }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({} as any));
      const display = data?.newOwner?.displayName || data?.newOwner?.email || selectedUid;
      onTransferred(selectedUid, display);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Transfer failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Transfer ownership"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
          width: 'min(480px, 92vw)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 'var(--radius-md, 8px)',
          border: '1px solid var(--border-color)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md, 16px)' }}>
            Transfer ownership of {kind === 'job' ? 'job' : 'resume'}
          </h3>
          <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
            <span title={itemLabel} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
              {itemLabel}
            </span>
          </p>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {eligible.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No other members available to receive ownership.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {eligible.map(m => {
                const checked = selectedUid === m.userId;
                const fullName = m.displayName || m.email || m.userId;
                return (
                  <label
                    key={m.userId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: checked ? 'rgba(59,130,246,0.08)' : 'transparent',
                      border: `1px solid ${checked ? 'var(--color-primary, #3b82f6)' : 'transparent'}`,
                    }}
                  >
                    <input
                      type="radio"
                      name="new-owner"
                      value={m.userId}
                      checked={checked}
                      onChange={() => setSelectedUid(m.userId)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <span
                        title={fullName}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}
                      >
                        {fullName}
                      </span>
                      {m.email && m.email !== fullName && (
                        <span
                          title={m.email}
                          style={{
                            color: 'var(--text-muted)',
                            fontSize: 'var(--font-size-xs)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {m.email}
                        </span>
                      )}
                    </span>
                    <span
                      className="v2-badge v2-badge-muted"
                      style={{ fontSize: 10, textTransform: 'capitalize' }}
                    >
                      {m.role}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                marginTop: 12,
                padding: '8px 10px',
                background: 'rgba(239,68,68,0.10)',
                color: '#ef4444',
                borderRadius: 6,
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button className="v2-btn v2-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="v2-btn v2-btn-primary"
            onClick={submit}
            disabled={submitting || !selectedUid || eligible.length === 0}
          >
            {submitting ? 'Transferring…' : 'Transfer ownership'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
